// Fix GW (2026-10-19): RRHH → Marcación. Panel de la marcación del personal con geolocalización:
// quién marcó hoy, alertas de intentos fuera del radio, ubicación y radio de cada obra, y PIN de acceso.
// Pedido de CEN: "un apartado de marcación de los personales con geolocalización" y que "no le permita
// luego marcar si no se encuentra en el radio de la obra correspondiente". El radio se edita por obra.
import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, RefreshCw, Loader2, X, Check, AlertTriangle, KeyRound, Copy, Navigation, Search,
  ExternalLink, Smartphone, Clock,
} from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { roleAllowed } from "../../utils/roles";

const ADMIN_ROLES = ["Superadministrador", "Administrador", "Jefe de RRHH"];
const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white transition-all";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5";
const btnPrimary = "inline-flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60";
const btnGhost = "inline-flex items-center justify-center gap-2 border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60";

const ESTADOS = {
  sin_marcar: { label: "Sin marcar", cls: "bg-gray-100 text-gray-600" },
  en_obra: { label: "En obra", cls: "bg-emerald-100 text-emerald-700" },
  almuerzo: { label: "En almuerzo", cls: "bg-amber-100 text-amber-700" },
  completa: { label: "Completa", cls: "bg-blue-100 text-blue-700" },
  incompleta: { label: "Incompleta", cls: "bg-red-100 text-red-700" },
};
const MOTIVOS = {
  fuera_de_radio: { label: "Fuera del radio", cls: "bg-red-100 text-red-700" },
  precision: { label: "GPS poco preciso", cls: "bg-amber-100 text-amber-700" },
  sin_ubicacion_obra: { label: "Obra sin ubicación", cls: "bg-purple-100 text-purple-700" },
  sin_obra: { label: "Sin obra asignada", cls: "bg-purple-100 text-purple-700" },
  sin_gps: { label: "Sin GPS / sin permiso", cls: "bg-gray-200 text-gray-700" },
};
const hoyPy = () => new Date().toLocaleDateString("sv-SE", { timeZone: "America/Asuncion" });   // AAAA-MM-DD
const ORIGEN = { convocatoria: "Convocatoria", asignada: "Obra asignada" };

const errMsg = (e, def) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.mensaje || def;
};
const fmtDist = (m) => (m == null ? "" : m >= 1000 ? `${(m / 1000).toFixed(1).replace(".", ",")} km` : `${Math.round(m)} m`);
const fmtFecha = (f) => (f ? f.split("-").reverse().join("/") : "");
const mapaUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

// "-25,2867, -57,6470" · "-25.2867 -57.6470" · enlace de Google Maps (@lat,lng · ?q=lat,lng · !3dlat!4dlng)
export function parsearCoordenadas(txt) {
  const t = String(txt || "").trim();
  if (!t) return null;
  const n = "(-?\\d{1,3}(?:[.,]\\d+)?)";
  const m = t.match(new RegExp(`!3d${n}!4d${n}`)) || t.match(new RegExp(`@${n},${n}`)) || t.match(new RegExp(`[?&]q=${n},${n}`))
    || t.match(new RegExp(`^${n}\\s*[,;\\s]\\s*${n}$`));
  if (!m) return null;
  const lat = parseFloat(m[1].replace(",", ".")), lng = parseFloat(m[2].replace(",", "."));
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}
const enParaguay = (lat, lng) => lat <= -19 && lat >= -28 && lng <= -54 && lng >= -63;

function Modal({ children, onClose, ancho = "max-w-lg", testid }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid={testid}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${ancho} max-h-[92vh] overflow-y-auto`}>
        <div className="flex justify-end px-4 pt-3"><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Cerrar"><X size={18} /></button></div>
        {children}
      </div>
    </div>,
    document.body
  );
}

// ─── HOY ───────────────────────────────────────────────────────────────────
function Hora({ m }) {
  if (!m) return <span className="text-gray-300">—</span>;
  return <div className="leading-tight"><span className="font-semibold tabular-nums text-gray-900">{m.hora}</span>{m.distancia_m != null && <span className="block text-[10px] text-gray-400">a {fmtDist(m.distancia_m)}</span>}
    {/* Fix IT (2026-11-02): marcada sin conexión y subida después; vale con la hora del celular. */}
    {m.sin_senal && <span className="block text-[10px] font-semibold text-amber-700" title="Se marcó sin conexión y se subió al volver la señal. Vale con la hora del celular." data-testid="marca-sin-senal">sin señal</span>}</div>;
}

function HoyView() {
  const [data, setData] = useState(null);
  const [fecha, setFecha] = useState(hoyPy);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("");
  const [q, setQ] = useState("");

  const cargar = useCallback(async (f) => {
    try {
      const { data: d } = await axios.get(`${API}/marcacion/admin/dia`, { params: f ? { fecha: f } : {}, withCredentials: true });
      setData(d);
    } catch (e) { toast.error(errMsg(e, "No se pudo cargar la marcación del día")); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { setLoading(true); cargar(fecha); }, [fecha, cargar]);
  useEffect(() => {   // el día en curso se actualiza solo cada minuto
    if (!data || data.fecha !== hoyPy()) return undefined;
    const id = setInterval(() => cargar(fecha), 60000);
    return () => clearInterval(id);
  }, [data, fecha, cargar]);

  const filas = useMemo(() => (data?.filas || []).filter((f) =>
    (!estado || f.estado === estado) && (!q.trim() || `${f.nombre} ${f.obra_nombre}`.toLowerCase().includes(q.trim().toLowerCase()))), [data, estado, q]);
  const r = data?.resumen;

  return (
    <div className="space-y-4" data-testid="marcacion-hoy">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className={labelCls}>Fecha</label><input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={inputCls} data-testid="marcacion-fecha" /></div>
        <div><label className={labelCls}>Estado</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className={inputCls} data-testid="marcacion-filtro-estado">
            <option value="">Todos</option>{Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></div>
        <div className="flex-1 min-w-[180px]"><label className={labelCls}>Buscar</label>
          <div className="relative"><Search size={14} className="absolute left-3 top-3.5 text-gray-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Trabajador u obra" className={`${inputCls} pl-9`} /></div></div>
        <button onClick={() => { setLoading(true); cargar(fecha); }} className={btnGhost} data-testid="marcacion-refresh"><RefreshCw size={15} />Actualizar</button>
      </div>
      {r && (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="marcacion-resumen">
          {[["Activos", r.activos, "text-gray-900"], ["Marcaron", r.marcaron, "text-emerald-600"], ["Completos", r.completos, "text-blue-600"],
            ["Sin marcar", r.sin_marcar, "text-gray-600"], ["Incompletos", r.incompletos, "text-red-600"],
            ["Alertas sin revisar", r.alertas_pendientes, r.alertas_pendientes ? "text-red-600" : "text-gray-400"], ["Sin PIN", r.sin_pin, r.sin_pin ? "text-amber-600" : "text-gray-400"]]
            .map(([l, v, c]) => <div key={l} className="bg-white rounded-xl border border-gray-200/60 p-3 shadow-sm"><p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{l}</p><p className={`text-xl font-bold tabular-nums ${c}`}>{v}</p></div>)}
        </div>
      )}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
            <th className="px-4 py-3">Trabajador</th><th className="px-3 py-3">Obra de hoy</th><th className="px-3 py-3">Entrada</th>
            <th className="px-3 py-3">Sale a almorzar</th><th className="px-3 py-3">Vuelve</th><th className="px-3 py-3">Salida</th>
            <th className="px-3 py-3">Estado</th><th className="px-3 py-3">Alertas</th></tr></thead>
          <tbody>
            {loading && !data ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400"><Loader2 className="inline animate-spin" size={16} /> Cargando…</td></tr>
              : filas.length === 0 ? <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Sin resultados</td></tr>
              : filas.map((f) => (
                <tr key={f.worker_id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`marcacion-fila-${f.worker_id}`}>
                  <td className="px-4 py-2.5"><p className="font-semibold text-gray-900">{f.nombre}</p><p className="text-xs text-gray-400">{f.cargo}{!f.tiene_pin && <span className="ml-2 text-amber-600 font-semibold">sin PIN</span>}</p></td>
                  <td className="px-3 py-2.5"><p className="text-gray-800">{f.obra_nombre || <span className="text-red-500">Sin obra</span>}</p>
                    <p className="text-[11px] text-gray-400">{ORIGEN[f.obra_origen] || ""}{f.obra_nombre && !f.obra_con_ubicacion && <span className="ml-1 text-purple-600 font-semibold">· sin ubicación</span>}</p></td>
                  <td className="px-3 py-2.5"><Hora m={f.marcas.entrada} /></td>
                  <td className="px-3 py-2.5"><Hora m={f.marcas.almuerzo_salida} /></td>
                  <td className="px-3 py-2.5"><Hora m={f.marcas.almuerzo_vuelta} /></td>
                  <td className="px-3 py-2.5"><Hora m={f.marcas.salida} /></td>
                  <td className="px-3 py-2.5"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ESTADOS[f.estado].cls}`}>{ESTADOS[f.estado].label}</span></td>
                  <td className="px-3 py-2.5">{f.alertas_pendientes ? <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><AlertTriangle size={14} />{f.alertas_pendientes}</span> : <span className="text-gray-300">—</span>}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">Las marcas aceptadas llenan el Parte Diario Adm. de ese día (origen «marcacion_gps»). Si una persona marcó solo entrada y salida, el almuerzo y los descansos del turno se descuentan solos.</p>
    </div>
  );
}

// ─── ALERTAS ───────────────────────────────────────────────────────────────
function AlertasView({ puedeRevisar, onCambio }) {
  const [estado, setEstado] = useState("pendientes");
  const [desde, setDesde] = useState(() => new Date(Date.now() - 6 * 864e5).toLocaleDateString("sv-SE", { timeZone: "America/Asuncion" }));
  const [hasta, setHasta] = useState(hoyPy);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revisando, setRevisando] = useState(null);
  const [nota, setNota] = useState("");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/marcacion/admin/alertas`, { params: { desde, hasta, estado }, withCredentials: true });
      setAlertas(data.alertas || []);
    } catch (e) { toast.error(errMsg(e, "No se pudieron cargar las alertas")); }
    finally { setLoading(false); }
  }, [desde, hasta, estado]);
  useEffect(() => { cargar(); }, [cargar]);

  const revisar = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/marcacion/admin/alertas/${revisando.id}/revisar`, { nota }, { withCredentials: true });
      toast.success("Alerta revisada");
      setRevisando(null); setNota(""); cargar(); onCambio?.();
    } catch (e) { toast.error(errMsg(e, "No se pudo marcar como revisada")); }
    finally { setGuardando(false); }
  };

  return (
    <div className="space-y-4" data-testid="marcacion-alertas">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className={labelCls}>Mostrar</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className={inputCls} data-testid="alertas-filtro-estado">
            <option value="pendientes">Sin revisar</option><option value="revisadas">Revisadas</option><option value="todas">Todas</option></select></div>
        <div><label className={labelCls}>Desde</label><input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={inputCls} /></div>
        <div><label className={labelCls}>Hasta</label><input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inputCls} /></div>
        <button onClick={cargar} className={btnGhost}><RefreshCw size={15} />Actualizar</button>
      </div>
      <div className="space-y-2">
        {loading ? <p className="text-center text-gray-400 py-8"><Loader2 className="inline animate-spin" size={16} /> Cargando…</p>
          : alertas.length === 0 ? <p className="text-center text-gray-400 py-8 bg-white rounded-xl border border-gray-200/60">No hay alertas en este período</p>
          : alertas.map((a) => {
            const m = MOTIVOS[a.motivo] || { label: a.motivo, cls: "bg-gray-100 text-gray-600" };
            return (
              <div key={a.id} className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm flex flex-wrap items-start gap-3 justify-between" data-testid={`alerta-${a.id}`}>
                <div className="min-w-[240px] flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>{m.label}</span>
                    <span className="font-semibold text-gray-900">{a.worker_name}</span>
                    <span className="text-xs text-gray-400">{fmtFecha(a.fecha)} {a.hora}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{a.mensaje}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {a.obra_nombre && <>Obra: {a.obra_nombre} · </>}
                    {a.distancia_m != null && <>Distancia {fmtDist(a.distancia_m)} (radio {fmtDist(a.radio_m)}) · </>}
                    {a.obra_detectada && <span className="text-purple-600 font-semibold">Estaba en la obra {a.obra_detectada} · </span>}
                    {a.lat != null && <a href={mapaUrl(a.lat, a.lng)} target="_blank" rel="noreferrer" className="text-[#2E6DA4] font-semibold inline-flex items-center gap-0.5">Ver dónde estaba <ExternalLink size={11} /></a>}
                  </p>
                  {a.revisada && <p className="text-xs text-emerald-700 mt-1">Revisada por {a.revisada_por}{a.nota ? ` — ${a.nota}` : ""}</p>}
                </div>
                {!a.revisada && puedeRevisar && <button onClick={() => { setRevisando(a); setNota(""); }} className={btnGhost} data-testid={`alerta-revisar-${a.id}`}><Check size={15} />Marcar revisada</button>}
              </div>);
          })}
      </div>
      {revisando && (
        <Modal onClose={() => setRevisando(null)} ancho="max-w-md" testid="alerta-modal">
          <div className="px-6 pb-6 space-y-4">
            <h3 className="text-base font-bold text-gray-900">Marcar alerta como revisada</h3>
            <p className="text-sm text-gray-600">{revisando.worker_name} · {fmtFecha(revisando.fecha)} {revisando.hora}. Si la persona sí estaba trabajando, corregí sus horas en el Parte Diario Adm.</p>
            <div><label className={labelCls}>Nota (opcional)</label><input value={nota} onChange={(e) => setNota(e.target.value)} maxLength={300} placeholder="Ej: estaba cargando materiales en el depósito" className={inputCls} data-testid="alerta-nota" /></div>
            <div className="flex justify-end gap-2"><button onClick={() => setRevisando(null)} className={btnGhost}>Cancelar</button>
              <button onClick={revisar} disabled={guardando} className={btnPrimary} data-testid="alerta-confirmar">{guardando ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}Revisada</button></div>
          </div>
        </Modal>)}
    </div>
  );
}

// ─── OBRAS Y RADIO ─────────────────────────────────────────────────────────
function ObraGeoModal({ obra, cfg, puedeEditar, onClose, onSaved }) {
  const [lat, setLat] = useState(obra.geo ? String(obra.geo.lat) : "");
  const [lng, setLng] = useState(obra.geo ? String(obra.geo.lng) : "");
  const [radio, setRadio] = useState(String(obra.geo?.radio_m || cfg.radio_defecto_m));
  const [pegado, setPegado] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [precision, setPrecision] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const nLat = parseFloat(String(lat).replace(",", ".")), nLng = parseFloat(String(lng).replace(",", "."));
  const valido = Number.isFinite(nLat) && Number.isFinite(nLng) && Math.abs(nLat) <= 90 && Math.abs(nLng) <= 180;
  const nRadio = parseInt(radio, 10);
  const radioOk = Number.isFinite(nRadio) && nRadio >= cfg.radio_min_m && nRadio <= cfg.radio_max_m;

  const pegar = (txt) => {
    setPegado(txt);
    const c = parsearCoordenadas(txt);
    if (c) { setLat(String(c.lat)); setLng(String(c.lng)); setPrecision(null); }
  };
  const usarMiUbicacion = () => {
    if (!navigator.geolocation) { toast.error("Este navegador no puede dar la ubicación"); return; }
    setBuscando(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); setPrecision(Math.round(p.coords.accuracy)); setBuscando(false); },
      () => { setBuscando(false); toast.error("No se pudo obtener la ubicación. Revisá el permiso del navegador."); },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 });
  };
  const guardar = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/marcacion/admin/obras/${obra.id}`, { lat: nLat, lng: nLng, radio_m: nRadio }, { withCredentials: true });
      toast.success("Ubicación y radio guardados"); onSaved();
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); }
    finally { setGuardando(false); }
  };
  const quitar = async () => {
    setGuardando(true);
    try { await axios.delete(`${API}/marcacion/admin/obras/${obra.id}/ubicacion`, { withCredentials: true }); toast.success("Se quitó la ubicación de la obra"); onSaved(); }
    catch (e) { toast.error(errMsg(e, "No se pudo quitar")); }
    finally { setGuardando(false); }
  };

  return (
    <Modal onClose={onClose} testid="obra-geo-modal">
      <div className="px-6 pb-6 space-y-4">
        <div><h3 className="text-base font-bold text-gray-900">Ubicación y radio — {obra.nombre}</h3>
          {obra.ubicacion_texto && <p className="text-xs text-gray-400 mt-0.5">{obra.ubicacion_texto}</p>}</div>
        {puedeEditar && (
          <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 text-xs text-blue-900 space-y-2">
            <p><b>Lo más preciso:</b> estando en la obra, desde un celular, tocá «Usar mi ubicación actual». También podés pegar las coordenadas o el enlace de Google Maps del punto.</p>
            <button type="button" onClick={usarMiUbicacion} disabled={buscando} className={btnPrimary} data-testid="obra-geo-mi-ubicacion">
              {buscando ? <Loader2 size={15} className="animate-spin" /> : <Navigation size={15} />}Usar mi ubicación actual</button>
            {precision != null && <p className={precision > 50 ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold"}>Precisión del celular: ±{precision} m{precision > 50 ? " — conviene repetirlo al aire libre o desde un celular." : "."}</p>}
          </div>)}
        <div><label className={labelCls}>Pegar coordenadas o enlace de Google Maps</label>
          <input value={pegado} onChange={(e) => pegar(e.target.value)} disabled={!puedeEditar} placeholder="-25.2867, -57.6470  ·  https://www.google.com/maps/@-25.28,-57.64,17z" className={inputCls} data-testid="obra-geo-pegar" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>Latitud</label><input value={lat} onChange={(e) => setLat(e.target.value)} disabled={!puedeEditar} inputMode="decimal" className={inputCls} data-testid="obra-geo-lat" /></div>
          <div><label className={labelCls}>Longitud</label><input value={lng} onChange={(e) => setLng(e.target.value)} disabled={!puedeEditar} inputMode="decimal" className={inputCls} data-testid="obra-geo-lng" /></div>
        </div>
        {valido && <a href={mapaUrl(nLat, nLng)} target="_blank" rel="noreferrer" className="text-xs text-[#2E6DA4] font-semibold inline-flex items-center gap-1">Ver este punto en el mapa <ExternalLink size={12} /></a>}
        {valido && !enParaguay(nLat, nLng) && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2" data-testid="obra-geo-fuera-pais">Ese punto queda fuera de Paraguay. Revisá que la latitud y la longitud tengan el signo correcto (en Paraguay las dos son negativas).</p>}
        <div><label className={labelCls}>Radio de marcación (metros) — de {cfg.radio_min_m} a {cfg.radio_max_m}</label>
          <div className="flex flex-wrap gap-2 items-center">
            <input value={radio} onChange={(e) => setRadio(e.target.value.replace(/\D/g, ""))} disabled={!puedeEditar} inputMode="numeric" className={`${inputCls} max-w-[120px]`} data-testid="obra-geo-radio" />
            {puedeEditar && [50, 100, 150, 200, 300].map((v) => <button key={v} type="button" onClick={() => setRadio(String(v))} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${String(v) === radio ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>{v} m</button>)}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Una obra grande necesita un radio mayor; una chica, uno menor. Solo se puede marcar dentro de este radio.</p></div>
        {puedeEditar ? (
          <div className="flex flex-wrap justify-between gap-2 pt-1">
            {obra.geo ? <button onClick={quitar} disabled={guardando} className="text-sm font-semibold text-red-600 hover:underline" data-testid="obra-geo-quitar">Quitar ubicación</button> : <span />}
            <div className="flex gap-2"><button onClick={onClose} className={btnGhost}>Cancelar</button>
              <button onClick={guardar} disabled={guardando || !valido || !radioOk} className={btnPrimary} data-testid="obra-geo-guardar">{guardando ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}Guardar</button></div>
          </div>) : <p className="text-xs text-gray-400">Solo RRHH y Administración pueden cambiar la ubicación.</p>}
      </div>
    </Modal>
  );
}

function ObrasView({ puedeEditar }) {
  const [cfg, setCfg] = useState(null);
  const [soloPersonal, setSoloPersonal] = useState(true);
  const [q, setQ] = useState("");
  const [editando, setEditando] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/marcacion/admin/obras`, { withCredentials: true }); setCfg(data); }
    catch (e) { toast.error(errMsg(e, "No se pudieron cargar las obras")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const obras = useMemo(() => (cfg?.obras || []).filter((o) => (!soloPersonal || o.personal > 0 || o.geo)
    && (!q.trim() || `${o.nombre} ${o.codigo} ${o.cliente}`.toLowerCase().includes(q.trim().toLowerCase()))), [cfg, soloPersonal, q]);
  const sinUbicacionConPersonal = (cfg?.obras || []).filter((o) => o.personal > 0 && !o.geo).length;

  return (
    <div className="space-y-4" data-testid="marcacion-obras">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]"><label className={labelCls}>Buscar obra</label>
          <div className="relative"><Search size={14} className="absolute left-3 top-3.5 text-gray-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, código o cliente" className={`${inputCls} pl-9`} /></div></div>
        <label className="flex items-center gap-2 text-sm text-gray-600 pb-2.5"><input type="checkbox" checked={soloPersonal} onChange={(e) => setSoloPersonal(e.target.checked)} />Solo obras con personal o ubicación</label>
      </div>
      {sinUbicacionConPersonal > 0 && <p className="text-sm text-purple-800 bg-purple-50 border border-purple-100 rounded-xl p-3" data-testid="obras-sin-ubicacion-aviso">
        <AlertTriangle size={14} className="inline mr-1" />{sinUbicacionConPersonal} obra{sinUbicacionConPersonal > 1 ? "s" : ""} con personal asignado todavía no tiene{sinUbicacionConPersonal > 1 ? "n" : ""} ubicación: su gente no podrá marcar hasta que la cargues.</p>}
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
            <th className="px-4 py-3">Obra</th><th className="px-3 py-3">Personal</th><th className="px-3 py-3">Ubicación</th><th className="px-3 py-3">Radio</th><th className="px-3 py-3" /></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400"><Loader2 className="inline animate-spin" size={16} /> Cargando…</td></tr>
              : obras.length === 0 ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Sin obras para mostrar</td></tr>
              : obras.map((o) => (
                <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`obra-fila-${o.id}`}>
                  <td className="px-4 py-2.5"><p className="font-semibold text-gray-900">{o.nombre}</p><p className="text-xs text-gray-400">{o.codigo}{o.cliente ? ` · ${o.cliente}` : ""}</p></td>
                  <td className="px-3 py-2.5 tabular-nums">{o.personal || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5">{o.geo ? <a href={mapaUrl(o.geo.lat, o.geo.lng)} target="_blank" rel="noreferrer" className="text-[#2E6DA4] inline-flex items-center gap-1 text-xs font-semibold"><MapPin size={13} />{o.geo.lat.toFixed(5)}, {o.geo.lng.toFixed(5)}</a>
                    : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Sin ubicación</span>}</td>
                  <td className="px-3 py-2.5 tabular-nums">{o.geo ? `${o.geo.radio_m} m` : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5 text-right"><button onClick={() => setEditando(o)} className={btnGhost} data-testid={`obra-editar-${o.id}`}><MapPin size={14} />{puedeEditar ? (o.geo ? "Editar" : "Cargar") : "Ver"}</button></td>
                </tr>))}
          </tbody>
        </table>
      </div>
      {editando && <ObraGeoModal obra={editando} cfg={cfg} puedeEditar={puedeEditar} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />}
    </div>
  );
}

// ─── ACCESOS (PIN) ─────────────────────────────────────────────────────────
function AccesosView({ puedeEditar }) {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [pines, setPines] = useState(null);       // [{nombre, cedula, pin}] — se muestran UNA vez
  const [confirmarLote, setConfirmarLote] = useState(false);
  const link = `${window.location.origin}/marcar`;

  const cargar = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/marcacion/admin/accesos`, { withCredentials: true }); setLista(data.trabajadores || []); }
    catch (e) { toast.error(errMsg(e, "No se pudieron cargar los accesos")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const copiar = (txt, ok = "Copiado") => navigator.clipboard?.writeText(txt).then(() => toast.success(ok)).catch(() => toast.error("No se pudo copiar"));
  const generar = async (w) => {
    setBusy(w.worker_id);
    try {
      const { data } = await axios.post(`${API}/marcacion/admin/pin/${w.worker_id}`, {}, { withCredentials: true });
      setPines({ lote: false, lista: [{ nombre: data.nombre, cedula: data.cedula, pin: data.pin }], omitidos: [] }); cargar();
    } catch (e) { toast.error(errMsg(e, "No se pudo generar el PIN")); }
    finally { setBusy(""); }
  };
  const generarLote = async () => {
    setBusy("lote"); setConfirmarLote(false);
    try {
      const { data } = await axios.post(`${API}/marcacion/admin/pin-lote`, {}, { withCredentials: true });
      if (!data.generados.length && !data.omitidos.length) toast.info("Todos los trabajadores activos ya tienen PIN");
      else setPines({ lote: true, lista: data.generados, omitidos: data.omitidos });
      cargar();
    } catch (e) { toast.error(errMsg(e, "No se pudieron generar los PIN")); }
    finally { setBusy(""); }
  };

  const filas = lista.filter((w) => !q.trim() || `${w.nombre} ${w.cedula}`.toLowerCase().includes(q.trim().toLowerCase()));
  const sinPin = lista.filter((w) => !w.tiene_pin && !w.sin_cedula).length;

  return (
    <div className="space-y-4" data-testid="marcacion-accesos">
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm space-y-2">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Smartphone size={16} className="text-[#2E6DA4]" />Pantalla de marcación del personal</p>
        <p className="text-sm text-gray-600">Cada trabajador entra desde su celular con su <b>cédula</b> y su <b>PIN</b>. Si no tiene celular, puede entrar con su cédula y su PIN desde el celular de un compañero. Solo puede marcar estando dentro del radio de su obra.</p>
        <div className="flex flex-wrap items-center gap-2"><code className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm" data-testid="marcacion-link">{link}</code>
          <button onClick={() => copiar(link, "Enlace copiado")} className={btnGhost}><Copy size={14} />Copiar enlace</button></div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]"><label className={labelCls}>Buscar</label>
          <div className="relative"><Search size={14} className="absolute left-3 top-3.5 text-gray-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o cédula" className={`${inputCls} pl-9`} /></div></div>
        {puedeEditar && <button onClick={() => setConfirmarLote(true)} disabled={!!busy || !sinPin} className={btnPrimary} data-testid="pin-lote-btn">
          {busy === "lote" ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}Generar PIN a los que no tienen ({sinPin})</button>}
      </div>
      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
            <th className="px-4 py-3">Trabajador</th><th className="px-3 py-3">Cédula</th><th className="px-3 py-3">Acceso</th><th className="px-3 py-3">Último ingreso</th><th className="px-3 py-3" /></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400"><Loader2 className="inline animate-spin" size={16} /> Cargando…</td></tr>
              : filas.map((w) => (
                <tr key={w.worker_id} className="border-b border-gray-50 hover:bg-gray-50/60" data-testid={`acceso-fila-${w.worker_id}`}>
                  <td className="px-4 py-2.5"><p className="font-semibold text-gray-900">{w.nombre}</p><p className="text-xs text-gray-400">{w.cargo}</p></td>
                  <td className="px-3 py-2.5">{w.cedula || <span className="text-red-500 text-xs font-semibold">Sin cédula</span>}</td>
                  <td className="px-3 py-2.5">{w.bloqueado ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Bloqueado (intentos fallidos)</span>
                    : w.tiene_pin ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Con PIN</span>
                    : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Sin PIN</span>}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{w.ultimo_acceso ? new Date(w.ultimo_acceso).toLocaleString("es-PY", { timeZone: "America/Asuncion" }) : "—"}</td>
                  <td className="px-3 py-2.5 text-right">{puedeEditar && !w.sin_cedula && <button onClick={() => generar(w)} disabled={!!busy} className={btnGhost} data-testid={`pin-generar-${w.worker_id}`}>
                    {busy === w.worker_id ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}{w.tiene_pin ? "Restablecer PIN" : "Generar PIN"}</button>}</td>
                </tr>))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">El PIN se muestra una sola vez al generarlo y no queda guardado en claro. Restablecerlo cierra las sesiones abiertas de esa persona y destraba la cuenta si estaba bloqueada.</p>

      {confirmarLote && (
        <Modal onClose={() => setConfirmarLote(false)} ancho="max-w-md" testid="pin-lote-modal">
          <div className="px-6 pb-6 space-y-4">
            <h3 className="text-base font-bold text-gray-900">Generar PIN a {sinPin} trabajador{sinPin > 1 ? "es" : ""}</h3>
            <p className="text-sm text-gray-600">Se crea un PIN de 4 dígitos para cada trabajador activo que todavía no tiene. Los PIN aparecen una sola vez: copialos y entregáselos a cada persona.</p>
            <div className="flex justify-end gap-2"><button onClick={() => setConfirmarLote(false)} className={btnGhost}>Cancelar</button>
              <button onClick={generarLote} className={btnPrimary} data-testid="pin-lote-confirmar"><KeyRound size={15} />Generar</button></div>
          </div>
        </Modal>)}
      {pines && (
        <Modal onClose={() => setPines(null)} testid="pin-resultado-modal">
          <div className="px-6 pb-6 space-y-4">
            <h3 className="text-base font-bold text-gray-900">{pines.lote ? "PIN generados" : "PIN generado"}</h3>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2.5"><AlertTriangle size={14} className="inline mr-1" />Anotalo ahora: no se vuelve a mostrar.</p>
            <div className="border border-gray-100 rounded-xl divide-y divide-gray-100">
              {pines.lista.map((p) => <div key={p.cedula + p.nombre} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div><p className="text-sm font-semibold text-gray-900">{p.nombre}</p><p className="text-xs text-gray-400">CI {p.cedula}</p></div>
                <span className="text-xl font-bold tracking-[0.3em] tabular-nums text-[#2E6DA4]" data-testid="pin-valor">{p.pin}</span></div>)}
            </div>
            {pines.omitidos?.length > 0 && <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2.5"><p className="font-semibold mb-1">No se generó PIN para:</p>
              {pines.omitidos.map((o) => <p key={o.nombre}>• {o.nombre}: {o.motivo}</p>)}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => copiar(pines.lista.map((p) => `${p.nombre} — CI ${p.cedula} — PIN ${p.pin}`).join("\n"), "Lista copiada")} className={btnGhost}><Copy size={14} />Copiar lista</button>
              <button onClick={() => setPines(null)} className={btnPrimary}>Listo</button></div>
          </div>
        </Modal>)}
    </div>
  );
}

// ─── PESTAÑA ───────────────────────────────────────────────────────────────
export default function MarcacionTab() {
  const { user } = useAuth();
  const puedeEditar = roleAllowed(user?.role, ADMIN_ROLES);
  const puedeRevisar = roleAllowed(user?.role, [...ADMIN_ROLES, "Director"]);
  const [vista, setVista] = useState("hoy");
  const [pendientes, setPendientes] = useState(null);

  const contar = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/marcacion/admin/alertas`, { params: { estado: "pendientes" }, withCredentials: true }); setPendientes((data.alertas || []).length); }
    catch { setPendientes(null); }
  }, []);
  useEffect(() => { contar(); }, [contar, vista]);

  const VISTAS = [
    { id: "hoy", label: "Hoy", icon: Clock },
    { id: "alertas", label: "Alertas", icon: AlertTriangle, badge: pendientes },
    { id: "obras", label: "Obras y radio", icon: MapPin },
    { id: "accesos", label: "Accesos (PIN)", icon: KeyRound },
  ];
  return (
    <div className="space-y-5" data-testid="marcacion-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-[#EBF2F9] flex items-center justify-center"><MapPin size={20} className="text-[#2E6DA4]" /></div>
        <div><h2 className="text-lg font-bold text-gray-900">Marcación con geolocalización</h2>
          <p className="text-xs text-gray-500">El personal marca desde el celular y solo dentro del radio de su obra. Lo marcado llena el Parte Diario Adm.</p></div>
      </div>
      <div className="flex gap-1.5 flex-wrap" data-testid="marcacion-vistas">
        {VISTAS.map((v) => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => setVista(v.id)} data-testid={`marcacion-vista-${v.id}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${vista === v.id ? "bg-[#2E6DA4] text-white shadow-sm" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              <Icon size={15} />{v.label}
              {v.badge > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{v.badge}</span>}
            </button>);
        })}
      </div>
      {vista === "hoy" && <HoyView />}
      {vista === "alertas" && <AlertasView puedeRevisar={puedeRevisar} onCambio={contar} />}
      {vista === "obras" && <ObrasView puedeEditar={puedeEditar} />}
      {vista === "accesos" && <AccesosView puedeEditar={puedeEditar} />}
    </div>
  );
}
